import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Camera, Eye, Layers, Loader2, MoreHorizontal, Plus, ScanLine, Search, Trash2, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CiChips, VerificationBadge } from "@/components/Badges";
import { usesLabel, usesOf } from "@/pages/LibraryPage";
import { BottlePhotoControl } from "@/components/BottlePhoto";
import { PigmentBottle } from "@/components/kit/PigmentBottle";
import { Shelf } from "@/components/kit/Organizer";
import { useElementWidth, useKitActions, useKitQuery } from "@/components/kit/useKit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "h-11 rounded-xl border-[#E6DBCF] bg-white/70 focus-visible:ring-[#C5A059]";
const pillSel = "h-11 rounded-full border-[#E6DBCF] bg-[#FDFBF7]";
const USE_OPTS = [["brows", "Brows"], ["lips", "Lips"], ["eyeliner", "Eyeliner"], ["correction", "Toning / correction"], ["areola", "Areola"], ["scar", "Scar camouflage"]];
const chunk = (arr, n) => Array.from({ length: Math.ceil(arr.length / n) }, (_, i) => arr.slice(i * n, i * n + n));
const usePigments = () => useQuery({ queryKey: ["pigments"], queryFn: async () => (await api.get("/pigments")).data });

function FromLibrary({ onAdd, kitIds }) {
  const [q, setQ] = useState("");
  const { data = [] } = usePigments();
  const list = useMemo(() => {
    const s = q.toLowerCase();
    return data.filter((p) => p.shade && `${p.brand} ${p.line} ${p.shade}`.toLowerCase().includes(s)).slice(0, 60);
  }, [data, q]);
  return (
    <div>
      <Input data-testid="kit-library-search" placeholder="Search brand, line or shade…" value={q} onChange={(e) => setQ(e.target.value)} className={inputCls} />
      <div className="mt-3 max-h-80 space-y-1 overflow-y-auto">
        {list.map((p) => {
          const owned = kitIds.has(p.id);
          return (
            <button key={p.id} disabled={owned} data-testid={`kit-library-option-${p.id}`} onClick={() => onAdd({ pigment_id: p.id })} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-[#FDF2F0] disabled:cursor-not-allowed disabled:opacity-50">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-[#2C1810]">{p.brand} · {p.shade}</p>
                <p className="truncate text-xs text-[#A88B7D]">{p.line} · {usesLabel(p)}</p>
              </div>
              {owned ? <span className="text-[10px] font-semibold uppercase tracking-wider text-[#2E6B4F]">In your kit</span> : <VerificationBadge status={p.verification_status} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function WholeLine({ onAddLine, kitIds }) {
  const { data = [] } = usePigments();
  const [brand, setBrand] = useState("");
  const [line, setLine] = useState("");
  const shaded = data.filter((p) => p.shade);
  const brands = [...new Set(shaded.map((p) => p.brand))].sort();
  const lines = [...new Set(shaded.filter((p) => p.brand === brand).map((p) => p.line))].sort();
  const shades = shaded.filter((p) => p.brand === brand && p.line === line);
  const missing = shades.filter((p) => !kitIds.has(p.id)).length;
  return (
    <div className="space-y-3">
      <Select value={brand} onValueChange={(v) => { setBrand(v); setLine(""); }}>
        <SelectTrigger data-testid="kit-line-brand" className={inputCls}><SelectValue placeholder="Choose a brand" /></SelectTrigger>
        <SelectContent>{brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={line} onValueChange={setLine} disabled={!brand}>
        <SelectTrigger data-testid="kit-line-line" className={inputCls}><SelectValue placeholder="Choose a pigment line" /></SelectTrigger>
        <SelectContent>{lines.map((l) => <SelectItem key={l} value={l}>{l || "(no line name)"}</SelectItem>)}</SelectContent>
      </Select>
      {line && (
        <div className="rounded-xl bg-[#F6F1EA] p-3 text-xs text-[#4A3B32]" data-testid="kit-line-summary">
          <p className="font-semibold">{shades.length} shades in this line · {shades.length - missing} already in your kit</p>
          <p className="mt-1 line-clamp-3 text-[#6E5549]">{shades.map((p) => p.shade).join(", ")}</p>
        </div>
      )}
      <button disabled={!line || !missing} onClick={() => onAddLine(brand, line)} data-testid="kit-line-submit" className="h-11 w-full rounded-full bg-[#2C1810] text-sm font-semibold text-[#FDFBF7] hover:bg-[#3D2317] disabled:opacity-40">
        {line ? (missing ? `Add ${missing} shade${missing === 1 ? "" : "s"} to my kit` : "Every shade is already in your kit") : "Add whole line"}
      </button>
    </div>
  );
}

function FreeText({ onAdd, initial, extra = {}, submitLabel = "Add to kit" }) {
  const [f, setF] = useState({ brand: "", line: "", shade: "", is_modifier: false, notes: "", ...initial });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form className="space-y-3" onSubmit={(e) => {
      e.preventDefault();
      if (!f.shade.trim() && !f.line.trim()) return toast.error("Add a shade name or a line name");
      return onAdd({ ...f, ...extra });
    }}>
      <Input data-testid="kit-free-brand" required placeholder="Brand (e.g. Perma Blend)" value={f.brand} onChange={set("brand")} className={inputCls} />
      <Input data-testid="kit-free-line" placeholder="Line (e.g. LUXE)" value={f.line} onChange={set("line")} className={inputCls} />
      <Input data-testid="kit-free-shade" placeholder="Exact shade name" value={f.shade} onChange={set("shade")} className={inputCls} />
      <Input data-testid="kit-free-notes" placeholder="Notes (lot, region, label CI if known)" value={f.notes} onChange={set("notes")} className={inputCls} />
      <label className="flex items-center gap-3 text-sm text-[#4A3B32]">
        <Switch data-testid="kit-free-modifier" checked={f.is_modifier} onCheckedChange={(v) => setF({ ...f, is_modifier: v })} /> Modifier / corrector
      </label>
      <p className="rounded-xl bg-[#F9ECEC] p-3 text-xs text-[#8C2A2A]">Free-typed entries are marked <b>USER-SUPPLIED NAME ONLY – NOT VERIFIED</b>. Ink Genius will not assign CI data to them.</p>
      <button type="submit" data-testid="kit-free-submit" className="h-11 w-full rounded-full bg-[#2C1810] text-sm font-semibold text-[#FDFBF7] hover:bg-[#3D2317]">{submitLabel}</button>
    </form>
  );
}

const CONF = { high: ["High match", "bg-[#E7F1EA] text-[#2E6B4F]"], medium: ["Likely match", "bg-[#F7EEDD] text-[#8A6420]"], low: ["Possible match", "bg-[#F6F1EA] text-[#6E5549]"] };

const readingText = (r) => [[r.brand, r.line].filter(Boolean).join(" · "), [r.shade_code, r.shade].filter(Boolean).join(" ")].filter(Boolean).join(" — ") || "Label partly unreadable";
const typedIn = (r) => ({ brand: r.brand, line: r.line, shade: [r.shade_code, r.shade].filter(Boolean).join(" "), notes: r.ci_numbers?.length ? `From label scan · CI on label: ${r.ci_numbers.join(", ")}` : "From label scan" });

const nkey = (b, l, sh) => [b, l, sh].map((v) => (v || "").toLowerCase().replace(/[^a-z0-9]/g, "")).join("|");
const typedKey = (r) => { const t = typedIn(r); return nkey(t.brand, t.line, t.shade); };
const canType = (r) => !!(r.brand && (r.shade || r.line));

function initialPick(b, kitIds, typedKeys) {
  const top = b.matches[0];
  if (top && (top.confidence !== "low" || !canType(b.reading))) {
    const owned = top.in_kit || kitIds.has(top.pigment.id);
    return { choice: top.pigment.id, on: !owned && top.confidence !== "low" };
  }
  if (canType(b.reading)) return { choice: "typed", on: !typedKeys.has(typedKey(b.reading)) };
  return { choice: "skip", on: false };
}

function MultiResults({ res, kitIds, typedKeys, onAddMany }) {
  const [picks, setPicks] = useState(() => res.bottles.map((b) => initialPick(b, kitIds, typedKeys)));
  const set = (i, patch) => setPicks((p) => p.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const items = picks.flatMap((p, i) => {
    if (!p.on || p.choice === "skip") return [];
    const b = res.bottles[i];
    if (p.choice === "typed") return typedKeys.has(typedKey(b.reading)) ? [] : [typedIn(b.reading)];
    if (b.matches.find((m) => m.pigment.id === p.choice && (m.in_kit || kitIds.has(m.pigment.id)))) return [];
    return [{ pigment_id: p.choice }];
  });
  if (!res.bottles.length) {
    return <p className="rounded-xl bg-[#F6F1EA] p-3 text-xs text-[#4A3B32]" data-testid="kit-scan-multi-empty">We couldn't read any pigment labels in this photo. Try a closer shot with labels facing the camera.</p>;
  }
  return (
    <div className="min-w-0 space-y-2" data-testid="kit-scan-multi">
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#A88B7D]">{res.bottles.length} bottle{res.bottles.length === 1 ? "" : "s"} found</p>
      {res.bottles.map((b, i) => {
        const p = picks[i];
        const m = b.matches.find((x) => x.pigment.id === p.choice);
        const owned = m ? m.in_kit || kitIds.has(m.pigment.id) : p.choice === "typed" && typedKeys.has(typedKey(b.reading));
        const [cl, cc] = m ? CONF[m.confidence] : p.choice === "typed" ? ["Not verified", "bg-[#F9ECEC] text-[#8C2A2A]"] : ["Skipped", "bg-[#F6F1EA] text-[#6E5549]"];
        return (
          <div key={`${i}-${readingText(b.reading)}`} className={`flex items-center gap-3 rounded-xl border p-3 ${p.on && !owned ? "border-[#C5A059] bg-[#FFFDF8]" : "border-[#E6DBCF]"}`} data-testid={`kit-scan-bottle-${i}`}>
            <input type="checkbox" checked={p.on && !owned && p.choice !== "skip"} disabled={owned || p.choice === "skip"} onChange={(e) => set(i, { on: e.target.checked })}
              className="h-4 w-4 shrink-0 accent-[#2C1810]" data-testid={`kit-scan-bottle-${i}-check`} aria-label="Include this bottle" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[11px] text-[#A88B7D]">Label: {readingText(b.reading)}</p>
              <Select value={p.choice} onValueChange={(v) => set(i, { choice: v, on: v !== "skip" })}>
                <SelectTrigger className="mt-1 h-9 rounded-lg border-[#E6DBCF] bg-white/70 text-left text-sm" data-testid={`kit-scan-bottle-${i}-select`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {b.matches.map((x) => <SelectItem key={x.pigment.id} value={x.pigment.id}>{x.pigment.brand} · {x.pigment.shade}{x.in_kit || kitIds.has(x.pigment.id) ? " (in your kit)" : ""}</SelectItem>)}
                  {canType(b.reading) && <SelectItem value="typed">Add as typed-in (not verified)</SelectItem>}
                  <SelectItem value="skip">Skip this bottle</SelectItem>
                </SelectContent>
              </Select>
              <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${owned ? "bg-[#E7F1EA] text-[#2E6B4F]" : cc}`} data-testid={`kit-scan-bottle-${i}-status`}>{owned ? "Already in your kit" : cl}</span>
            </div>
          </div>
        );
      })}
      <button disabled={!items.length} onClick={() => onAddMany(items)} data-testid="kit-scan-add-many"
        className="mt-2 h-11 w-full rounded-full bg-[#2C1810] text-sm font-semibold text-[#FDFBF7] hover:bg-[#3D2317] disabled:opacity-40">
        {items.length ? `Add ${items.length} to kit` : "Nothing selected to add"}
      </button>
    </div>
  );
}

const SCAN_AREAS = [["brows", "Brows"], ["lips", "Lips"], ["any", "Any area"]];

function Segmented({ value, onChange, opts, testId }) {
  return (
    <div className="inline-flex rounded-full bg-[#F6F1EA] p-1" data-testid={testId}>
      {opts.map(([v, l]) => (
        <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testId}-${v}`} aria-pressed={value === v}
          className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${value === v ? "bg-[#2C1810] text-[#FDFBF7]" : "text-[#6E5549] hover:text-[#2C1810]"}`}>{l}</button>
      ))}
    </div>
  );
}

function ScanLabel({ kitIds, typedKeys, onAdd, onAddMany }) {
  const input = useRef(null);
  const [area, setAreaState] = useState(() => localStorage.getItem("ig-scan-area") || "any");
  const setArea = (v) => { localStorage.setItem("ig-scan-area", v); setAreaState(v); };
  const [mode, setMode] = useState("single");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [res, setRes] = useState(null);
  const [usePhoto, setUsePhoto] = useState(true);
  const [manual, setManual] = useState(false);
  const scan = async (file) => {
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setRes(null);
    setManual(false);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("area", area);
      fd.append("mode", mode);
      const { data } = await api.post("/kit/scan-label", fd);
      setRes(data);
      setManual(data.mode === "single" && data.matches.every((m) => m.confidence === "low"));
    } catch (e) {
      toast.error(errMsg(e), { action: { label: "Retry", onClick: () => scan(file) } });
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };
  const r = res?.reading;
  const photo = usePhoto && res?.file_id ? { photo_file_id: res.file_id } : {};
  const changeOpt = (fn) => (v) => { fn(v); setRes(null); setPreview(null); };
  return (
    <div className="min-w-0 space-y-4" data-testid="kit-scan">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-2"><span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#A88B7D]">Scanning for</span>
          <Segmented value={area} onChange={changeOpt(setArea)} opts={SCAN_AREAS} testId="kit-scan-area" /></div>
        <Segmented value={mode} onChange={changeOpt(setMode)} opts={[["single", "One bottle"], ["multi", "Several bottles"]]} testId="kit-scan-mode" />
      </div>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => scan(e.target.files?.[0])} data-testid="kit-scan-input" />
      <div className="flex items-center gap-4">
        {preview ? <img src={preview} alt="Label preview" className="h-24 w-20 shrink-0 rounded-xl object-cover ring-1 ring-[#E6DBCF]" data-testid="kit-scan-preview" />
          : <div className="flex h-24 w-20 shrink-0 items-center justify-center rounded-xl border border-dashed border-[#D99B90] bg-[#FDF2F0] text-[#C9A28F]"><ScanLine size={22} /></div>}
        <div className="min-w-0 flex-1">
          <p className="text-sm text-[#4A3B32]">{mode === "multi" ? "Line up to 12 bottles with labels facing the camera. Ink Genius reads each label and matches it to the library." : "Snap the front label in good light. Ink Genius reads the brand, line, shade and any CI numbers, then finds it in the library."}</p>
          <button onClick={() => input.current?.click()} disabled={busy} data-testid="kit-scan-btn" className="mt-2 inline-flex h-10 items-center gap-2 rounded-full bg-[#C5A059] px-5 text-sm font-semibold text-[#2C1810] hover:bg-[#d4b273] disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />} {busy ? (mode === "multi" ? "Reading labels…" : "Reading label…") : preview ? (mode === "multi" ? "Scan another photo" : "Scan another label") : mode === "multi" ? "Snap or upload bottles" : "Snap or upload label"}
          </button>
        </div>
      </div>
      {res?.mode === "multi" && <MultiResults key={preview} res={res} kitIds={kitIds} typedKeys={typedKeys} onAddMany={onAddMany} />}
      {r && (
        <div className="rounded-xl bg-[#F6F1EA] p-3 text-xs text-[#4A3B32]" data-testid="kit-scan-reading">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#A88B7D]">What we read</p>
          {r.legible ? (
            <p className="mt-1"><b>{[r.brand, r.line].filter(Boolean).join(" · ") || "Brand not readable"}</b> — {[r.shade_code, r.shade].filter(Boolean).join(" ") || "shade not readable"}{r.ci_numbers?.length ? ` · CI ${r.ci_numbers.join(", ")}` : ""}</p>
          ) : <p className="mt-1">We couldn't read a pigment label in this photo. Try a closer, sharper shot of the front label.</p>}
        </div>
      )}
      {res?.mode === "single" && (
        <label className="flex items-center gap-3 text-sm text-[#4A3B32]">
          <Switch checked={usePhoto} onCheckedChange={setUsePhoto} data-testid="kit-scan-use-photo" /> Use this photo as my bottle photo
        </label>
      )}
      {res?.matches?.length > 0 && (
        <div className="space-y-2" data-testid="kit-scan-matches">
          {res.matches.map((m, i) => {
            const p = m.pigment;
            const owned = m.in_kit || kitIds.has(p.id);
            const [cl, cc] = CONF[m.confidence];
            return (
              <div key={p.id} className={`flex items-center gap-3 rounded-xl border p-3 ${i === 0 ? "border-[#C5A059] bg-[#FFFDF8]" : "border-[#E6DBCF]"}`} data-testid={`kit-scan-match-${i}`}>
                <div className="w-10 shrink-0"><PigmentBottle item={{ brand: p.brand, line: p.line, shade: p.shade, pigment: p }} compact /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-[#2C1810]">{p.brand} · {p.shade}</p>
                  <p className="truncate text-xs text-[#A88B7D]">{p.line} · {usesLabel(p)}</p>
                  <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${cc}`} data-testid={`kit-scan-match-${i}-confidence`}>{i === 0 ? "Best match · " : ""}{cl}</span>
                </div>
                <button disabled={owned} onClick={() => onAdd({ pigment_id: p.id, ...photo })} data-testid={`kit-scan-add-${i}`} className="shrink-0 rounded-full bg-[#2C1810] px-4 py-2 text-xs font-semibold text-[#FDFBF7] hover:bg-[#3D2317] disabled:bg-[#E6DBCF] disabled:text-[#6E5549]">
                  {owned ? "In your kit" : "Add to kit"}
                </button>
              </div>
            );
          })}
          {!manual && <button onClick={() => setManual(true)} className="text-xs text-[#9C6218] hover:underline" data-testid="kit-scan-none-match">None of these — add it as typed in</button>}
        </div>
      )}
      {res && manual && r?.legible && (
        <div className="rounded-2xl border border-[#E6DBCF] p-4" data-testid="kit-scan-manual">
          <p className="mb-3 text-sm text-[#4A3B32]">{res.matches.length ? "Add it as a typed-in pigment" : "No library match found. Check the details and add it as a typed-in pigment"}. It will be marked <b>not verified</b>.</p>
          <FreeText key={res.file_id} extra={photo} submitLabel="Add as not-verified pigment"
            initial={{ brand: r.brand, line: r.line, shade: [r.shade_code, r.shade].filter(Boolean).join(" "), notes: r.ci_numbers?.length ? `From label scan · CI on label: ${r.ci_numbers.join(", ")}` : "From label scan" }}
            onAdd={onAdd} />
        </div>
      )}
    </div>
  );
}

function AddDialog({ open, onOpenChange, kit, actions, onAdded, onAddedLine }) {
  const kitIds = useMemo(() => new Set(kit.map((k) => k.pigment_id).filter(Boolean)), [kit]);
  const typedKeys = useMemo(() => new Set(kit.filter((k) => !k.pigment_id).map((k) => nkey(k.brand, k.line, k.shade))), [kit]);
  const done = (cb) => (r) => {
    onOpenChange(false);
    setTimeout(() => cb(r), 250);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto overflow-x-hidden bg-[#FDFBF7] [&>*]:min-w-0" data-testid="kit-add-dialog">
        <DialogHeader><DialogTitle className="font-serif text-3xl">Add a pigment</DialogTitle></DialogHeader>
        <Tabs defaultValue="library" className="min-w-0">
          <TabsList className="grid h-auto w-full grid-cols-2 gap-1 rounded-3xl bg-[#F6F1EA] p-1 sm:grid-cols-4 sm:rounded-full">
            <TabsTrigger value="scan" data-testid="kit-tab-scan" className="rounded-full"><ScanLine size={14} className="mr-1" /> Scan label</TabsTrigger>
            <TabsTrigger value="library" data-testid="kit-tab-library" className="rounded-full">Single shade</TabsTrigger>
            <TabsTrigger value="line" data-testid="kit-tab-line" className="rounded-full">Whole line</TabsTrigger>
            <TabsTrigger value="free" data-testid="kit-tab-free" className="rounded-full">Type it in</TabsTrigger>
          </TabsList>
          <TabsContent value="library" className="mt-4 min-w-0"><FromLibrary kitIds={kitIds} onAdd={(p) => actions.add(p, done(onAdded))} /></TabsContent>
          <TabsContent value="line" className="mt-4 min-w-0"><WholeLine kitIds={kitIds} onAddLine={(b, l) => actions.addLine(b, l, done(onAddedLine))} /></TabsContent>
          <TabsContent value="scan" className="mt-4 min-w-0"><ScanLabel kitIds={kitIds} typedKeys={typedKeys} onAdd={(p) => actions.add(p, done(onAdded))} onAddMany={(items) => actions.addMany(items, done(onAddedLine))} /></TabsContent>
          <TabsContent value="free" className="mt-4 min-w-0"><FreeText onAdd={(p) => actions.add(p, done(onAdded))} /></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function PhotoPrompt({ item, onClose, actions }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/kit/${item.id}/photo`, fd);
      await actions.refresh();
      toast.success("Bottle photo saved");
      onClose();
    } catch (e) {
      toast.error(`Could not save the photo: ${errMsg(e)}`, { action: { label: "Retry", onClick: () => upload(file) } });
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm bg-[#FDFBF7] text-center" data-testid="kit-photo-prompt">
        {item && (
          <>
            <DialogHeader><DialogTitle className="text-center font-serif text-3xl">Add your bottle photo</DialogTitle></DialogHeader>
            <div className="mx-auto w-24"><PigmentBottle item={item} testId="kit-photo-prompt-bottle" /></div>
            <p className="text-sm text-[#4A3B32]"><b>{item.shade || item.line}</b> is in your kit. Snap a photo of your actual bottle so your organizer and consultation cards show the exact one you use.</p>
            <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="kit-photo-prompt-input" />
            <button onClick={() => input.current?.click()} disabled={busy} data-testid="kit-photo-prompt-upload" className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#C5A059] px-6 text-sm font-semibold text-[#2C1810] transition-colors hover:bg-[#d4b273] disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />} {busy ? "Saving…" : "Take or choose photo"}
            </button>
            <button onClick={onClose} disabled={busy} data-testid="kit-photo-prompt-skip" className="text-sm text-[#6E5549] hover:text-[#2C1810]">Skip for now</button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailsDialog({ item, onOpenChange, actions }) {
  const p = item?.pigment;
  return (
    <Dialog open={!!item} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto bg-[#FDFBF7]" data-testid="kit-details-dialog">
        {item && (
          <>
            <DialogHeader><DialogTitle className="font-serif text-3xl">{item.shade || item.line}</DialogTitle></DialogHeader>
            <div className="flex gap-5">
              <div className="w-24 shrink-0"><PigmentBottle item={item} testId="kit-details-bottle" /></div>
              <div className="min-w-0 space-y-2 text-sm text-[#4A3B32]">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#A88B7D]">{[item.brand, item.line].filter(Boolean).join(" · ")}{item.is_modifier ? " · modifier" : ""}</p>
                <VerificationBadge status={p ? p.verification_status : "name_only"} testId="kit-details-verification" />
                {p && <p className="text-xs text-[#9C6218]">{usesLabel(p)}</p>}
                {p?.ci_numbers?.length > 0 && <CiChips ci={p.ci_numbers} />}
                {p?.description && <p className="text-xs leading-relaxed text-[#6E5549]">{p.description.replace(/^Manufacturer description:\s*/, "")}</p>}
                {item.notes && <p className="text-xs italic text-[#6E5549]">{item.notes}</p>}
              </div>
            </div>
            <div className="mt-2 rounded-2xl bg-[#F6F1EA] p-4">
              <p className="text-sm font-medium text-[#2C1810]">My bottle photo</p>
              <p className="mb-2 text-xs text-[#6E5549]">{item.image_file_id ? "Your photo is shown on your organizer and consultation cards." : p?.image_file_id ? "Showing the library photo. Add your own for your exact bottle." : "Add a photo so your cards show your actual bottle."}</p>
              <BottlePhotoControl fileId={item.image_file_id} endpoint={`/kit/${item.id}/photo`} onChange={actions.refresh} testId={`kit-photo-${item.id}`} label="my bottle photo" highlight />
            </div>
            <button onClick={() => { onOpenChange(false); actions.remove(item); }} data-testid="kit-details-remove" className="mt-2 inline-flex items-center gap-2 self-start text-sm text-[#8C2A2A] hover:underline"><Trash2 size={14} /> Remove from kit</button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PhotoReminder({ kit, onJump }) {
  const { user } = useAuth();
  const key = `ig-photo-reminder-${user?.id}`;
  const missing = kit.filter((k) => !k.image_file_id);
  const [dismissed, setDismissed] = useState(() => JSON.parse(localStorage.getItem(key) || "[]"));
  if (!missing.length || missing.every((k) => dismissed.includes(k.id))) return null;
  const dismiss = () => {
    const ids = missing.map((k) => k.id);
    localStorage.setItem(key, JSON.stringify(ids));
    setDismissed(ids);
  };
  return (
    <div className="ig-rise mt-6 flex flex-col gap-4 rounded-2xl border border-[#E8BDB5] bg-[#FDF2F0] p-5 sm:flex-row sm:items-center" data-testid="kit-photo-reminder">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#2C1810] text-[#FDFBF7]"><Camera size={18} /></span>
      <div className="min-w-0 flex-1">
        <p className="font-serif text-xl font-semibold text-[#2C1810]" data-testid="kit-photo-reminder-count">{missing.length} of {kit.length} pigment{kit.length > 1 ? "s" : ""} {missing.length > 1 ? "have" : "has"} no bottle photo</p>
        <p className="text-sm text-[#6E5549]">Add a photo of your real bottle so your consultation cards show the exact bottle you'll be using.</p>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => onJump(missing[0])} data-testid="kit-photo-reminder-jump" className="rounded-full bg-[#2C1810] px-5 py-2.5 text-sm font-semibold text-[#FDFBF7] transition-colors hover:bg-[#3D2317]">Add photos</button>
        <button onClick={dismiss} data-testid="kit-photo-reminder-dismiss" aria-label="Dismiss reminder" className="rounded-full p-2 text-[#A88B7D] transition-colors hover:bg-white hover:text-[#2C1810]"><X size={16} /></button>
      </div>
    </div>
  );
}

function Caption({ k, onDetails, actions }) {
  const lineCount = k.lineCount;
  return (
    <div className="flex items-start justify-center gap-1" data-testid={`kit-item-${k.id}`}>
      <button onClick={() => onDetails(k)} className="min-w-0 text-center" data-testid={`kit-details-${k.id}`}>
        <span className="block truncate text-sm font-medium text-[#2C1810]">{k.shade || k.line}</span>
        <span className="block truncate text-[11px] text-[#A88B7D]">{k.brand}</span>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger data-testid={`kit-item-menu-${k.id}`} className="rounded-full p-1 text-[#6E5549] hover:bg-[#F6F1EA]" aria-label="Pigment options"><MoreHorizontal size={16} /></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[#FDFBF7]">
          <DropdownMenuItem onClick={() => onDetails(k)} data-testid={`kit-view-${k.id}`}><Eye size={14} className="mr-2" /> View details</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onDetails(k)} data-testid={`kit-photo-menu-${k.id}`}><Camera size={14} className="mr-2" /> {k.image_file_id ? "Replace photo" : "Add bottle photo"}</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => actions.remove(k)} data-testid={`kit-delete-${k.id}`} className="text-[#B42318] focus:text-[#B42318]"><Trash2 size={14} className="mr-2" /> Remove from kit</DropdownMenuItem>
          {lineCount > 1 && (
            <DropdownMenuItem onClick={() => actions.removeLine(k.brand, k.line)} data-testid={`kit-delete-line-${k.id}`} className="text-[#B42318] focus:text-[#B42318]"><Layers size={14} className="mr-2" /> Remove whole line ({lineCount})</DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default function KitPage() {
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(params.get("add") === "1");
  const [detailsId, setDetailsId] = useState(null);
  const [photoFor, setPhotoFor] = useState(null);
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("all");
  const [line, setLine] = useState("all");
  const [area, setArea] = useState("all");
  const { data: kit = [], isLoading: loading, isError, refetch, isFetching } = useKitQuery();
  const isLoading = loading || isError;
  const actions = useKitActions();
  const [ref, width] = useElementWidth();
  const perRow = width < 520 ? 2 : width < 780 ? 3 : 4;

  useEffect(() => {
    if (params.get("add") === "1") setParams({}, { replace: true });
  }, [params, setParams]);
  const details = detailsId ? kit.find((k) => k.id === detailsId) || null : null;
  const setDetails = (k) => setDetailsId(k?.id || null);

  const brands = useMemo(() => [...new Set(kit.map((k) => k.brand))].sort(), [kit]);
  const lines = useMemo(() => [...new Set(kit.filter((k) => brand === "all" || k.brand === brand).map((k) => k.line).filter(Boolean))].sort(), [kit, brand]);
  const list = useMemo(() => {
    const s = q.toLowerCase();
    const counts = kit.reduce((m, k) => ({ ...m, [`${k.brand}|${k.line}`]: (m[`${k.brand}|${k.line}`] || 0) + 1 }), {});
    return kit
      .filter((k) => (brand === "all" || k.brand === brand) && (line === "all" || k.line === line) &&
        (area === "all" || (k.pigment ? usesOf(k.pigment).includes(area) : false)) &&
        `${k.brand} ${k.line} ${k.shade} ${k.notes}`.toLowerCase().includes(s))
      .map((k) => ({ ...k, lineCount: k.line ? counts[`${k.brand}|${k.line}`] : 0 }));
  }, [kit, q, brand, line, area]);
  const lineSelected = line !== "all" && kit.filter((k) => k.line === line && (brand === "all" || k.brand === brand));
  const lineBrands = lineSelected ? [...new Set(lineSelected.map((k) => k.brand))] : [];

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 sm:px-10 lg:py-14">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-serif text-4xl font-semibold text-[#2C1810] sm:text-5xl">My Pigment Kit</h1>
          <span className="rounded-full border border-[#E3D3B5] bg-[#F7EEDD] px-3.5 py-1 text-sm text-[#6B5434]" data-testid="kit-count">{isLoading ? "…" : `${kit.length} pigment${kit.length === 1 ? "" : "s"}`}</span>
        </div>
        <button onClick={() => setOpen(true)} data-testid="kit-add-btn" className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#C5A059] px-7 text-sm font-semibold text-[#2C1810] transition-colors hover:bg-[#d4b273]"><Plus size={17} /> Add pigment</button>
      </div>
      <p className="mt-2 text-sm text-[#6E5549]">The pigments you own. Ink Genius reads your kit automatically and always looks for a kit-first option.</p>
      {kit.length > 0 && <PhotoReminder kit={kit} onJump={setDetails} />}
      {kit.length > 0 && (
        <div className="mt-6 flex flex-col gap-3 lg:flex-row">
          <div className="relative flex-1">
            <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A88B7D]" />
            <Input data-testid="kit-search" placeholder="Search your pigments…" value={q} onChange={(e) => setQ(e.target.value)} className={`${pillSel} pl-11`} />
          </div>
          <Select value={brand} onValueChange={(v) => { setBrand(v); setLine("all"); }}>
            <SelectTrigger data-testid="kit-brand-filter" className={`${pillSel} lg:w-44`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All brands</SelectItem>{brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={line} onValueChange={setLine}>
            <SelectTrigger data-testid="kit-line-filter" className={`${pillSel} lg:w-52`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All pigment lines</SelectItem>{lines.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={area} onValueChange={setArea}>
            <SelectTrigger data-testid="kit-area-filter" className={`${pillSel} lg:w-48`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All treatment areas</SelectItem>{USE_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {lineSelected && lineBrands.length === 1 && (
        <div className="mt-3 flex items-center justify-between rounded-xl bg-[#F6F1EA] px-4 py-2.5 text-sm text-[#4A3B32]" data-testid="kit-line-bar">
          <span>{lineSelected.length} saved from {lineBrands[0]} · {line}</span>
          <button onClick={() => { actions.removeLine(lineBrands[0], line); setLine("all"); }} data-testid="kit-remove-line-btn" className="inline-flex items-center gap-1.5 text-[#B42318] hover:underline"><Trash2 size={14} /> Remove whole line</button>
        </div>
      )}
      {isError && (
        <div className="mt-8 rounded-2xl border border-dashed border-[#E8BDB5] bg-[#FDF2F0] p-8 text-center" data-testid="kit-error">
          <p className="font-serif text-2xl text-[#2C1810]">We couldn't load your kit</p>
          <p className="mt-1 text-sm text-[#6E5549]">Your saved pigments are safe. Please try again.</p>
          <button onClick={() => refetch()} disabled={isFetching} data-testid="kit-retry" className="mt-4 rounded-full bg-[#2C1810] px-6 py-2.5 text-sm font-semibold text-[#FDFBF7] disabled:opacity-60">{isFetching ? "Retrying…" : "Retry"}</button>
        </div>
      )}
      <div ref={ref} className="mt-8 space-y-10" data-testid="kit-list">
        {chunk(list, perRow).map((row, i) => (
          <Shelf key={row[0].id} items={row} perRow={perRow} bottleMax={124} testId={`kit-shelf-${i}`}
            renderCaption={(k) => <Caption k={k} onDetails={setDetails} actions={actions} />} />
        ))}
        {!isLoading && kit.length > 0 && !list.length && <p className="py-10 text-center text-sm italic text-[#A88B7D]" data-testid="kit-no-match">No pigments match your filters.</p>}
        {!isLoading && !kit.length && (
          <div className="rounded-2xl border border-dashed border-[#D99B90] bg-[#FDF2F0] p-10 text-center" data-testid="kit-empty">
            <p className="font-serif text-2xl text-[#2C1810]">Your kit is empty</p>
            <p className="mt-1 text-sm text-[#6E5549]">Add single shades or a whole line from the library, or type them in.</p>
            <button onClick={() => setOpen(true)} data-testid="kit-empty-add-btn" className="mt-5 inline-flex h-11 items-center gap-2 rounded-full bg-[#C5A059] px-6 text-sm font-semibold text-[#2C1810] hover:bg-[#d4b273]"><Plus size={16} /> Add your first pigment</button>
          </div>
        )}
      </div>
      <AddDialog open={open} onOpenChange={setOpen} kit={kit} actions={actions}
        onAdded={(item) => item?.id && !item.image_file_id && setPhotoFor(item.id)}
        onAddedLine={(d) => d.added?.length && toast("Add bottle photos for your new shades?", {
          duration: 10000, action: { label: "Add photos", onClick: () => setPhotoFor(d.added[0].id) },
        })} />
      <PhotoPrompt item={photoFor && kit.find((k) => k.id === photoFor)} onClose={() => setPhotoFor(null)} actions={actions} />
      <DetailsDialog item={details} onOpenChange={(o) => !o && setDetails(null)} actions={actions} />
    </div>
  );
}
